import { useState } from "react"
import { Grid, Box, IconButton, Card, CardHeader, CardContent, Tooltip, CircularProgress, Alert } from "@mui/material"
import DeleteIcon from "@mui/icons-material/Delete"
import EditIcon from "@mui/icons-material/Edit"
import ConfirmDialog from "components/common/confirm-dialog"
import AlertDialog from "components/common/alert-dialog"
import { GridContainer, InfoText } from "components/common-styles"
import { rootStore, useStores } from "stores/root-store"
import { useNavigate } from "react-router-dom"
import { formatStandardDate } from "utils/date-helper"
import StatusComponent from "components/common/status-component"
import { DirectionsRun, PauseCircleOutline } from "@mui/icons-material"
import { trackEvent } from "utils/analytics"

const SaleOrderDeliveryInfo = ({ onEditClicked }) => {
  const { SaleOrderStore } = useStores()
  const deliveryInfo = { ...SaleOrderStore.deliveryInfo }
  const navigate = useNavigate()

  const [openDelete, setOpenDelete] = useState(false)
  const [openAlert, setOpenAlert] = useState(false)
  const [alertContent, setAlertContent] = useState("")
  const [isUpdatingHold, setIsUpdatingHold] = useState(false)

  const onDeleteClicked = () => {
    setOpenDelete(true)
  }

  const onConfirmDelete = async () => {
    const deleted = await SaleOrderStore.deleteSaleOrder()
    trackEvent("record_delete", { entity: "sales_order", count: 1, bulk: "false", result: deleted ? "success" : "failed" })
    if (deleted) {
      navigate("/sales-order")
    } else {
      setAlertContent("This order could not be deleted. Please try again.")
      setOpenAlert(true)
    }
  }

  const setOnOrOffHold = async setOnHold => {
    if (isUpdatingHold) return

    setIsUpdatingHold(true)
    try {
      const result = await SaleOrderStore.setOnOrOffHold(setOnHold)
      // beacon so the hit survives the page reload below
      trackEvent("order_hold", { action: setOnHold ? "on" : "off", result: result ? "success" : "failed", transport_type: "beacon" })

      if (result) {
        location.reload()
        return
      }

      const msg = "Failed to update the order status. Please try again."
      setAlertContent(msg)
      setOpenAlert(true)
    } catch {
      trackEvent("order_hold", { action: setOnHold ? "on" : "off", result: "failed", error_type: "exception" })
      const msg = "Failed to update the order status. Please try again."
      setAlertContent(msg)
      setOpenAlert(true)
    } finally {
      setIsUpdatingHold(false)
    }
  }

  if (isUpdatingHold) {
    return (
      <Card>
        <CardHeader title="Delivery Information" />
        <CardContent>
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: 220, gap: 2 }}>
            <CircularProgress size={36} />
            <InfoText>Updating order status...</InfoText>
          </Box>
        </CardContent>
      </Card>
    )
  }

  // An Exception order can still be anywhere in Scale (even Closed), so it is
  // only editable while its Scale status (wms_status) is still In Pool.
  const editableStatuses = ["In Pool", "On Hold", "Exception"]
  const isEditable = editableStatuses.includes(deliveryInfo?.order_status) && (deliveryInfo?.order_status !== "Exception" || deliveryInfo?.wms_status === "In Pool")
  const isLockedException = deliveryInfo?.order_status === "Exception" && !isEditable

  return (
    <>
      <Card>
        <CardHeader
          title="Delivery Information"
          action={
            isEditable && (
              <Box>
                {deliveryInfo?.order_status == "On Hold" ? (
                  <Tooltip placement="top" title="Release from Hold">
                    <IconButton id="btnReleaseHold" size="large" onClick={() => setOnOrOffHold(false)}>
                      <DirectionsRun fontSize="inherit" />
                    </IconButton>
                  </Tooltip>
                ) : (
                  <Tooltip placement="top" title="Put on Hold">
                    <IconButton id="btnPutOnHold" size="large" onClick={() => setOnOrOffHold(true)}>
                      <PauseCircleOutline fontSize="inherit" />
                    </IconButton>
                  </Tooltip>
                )}

                <Tooltip placement="top" title="Edit">
                  <IconButton id="btnEdit" size="large" onClick={onEditClicked}>
                    <EditIcon fontSize="inherit" />
                  </IconButton>
                </Tooltip>

                <Tooltip placement="top" title="Delete">
                  <IconButton id="btnDelete" size="large" onClick={onDeleteClicked}>
                    <DeleteIcon fontSize="inherit" />
                  </IconButton>
                </Tooltip>
              </Box>
            )
          }
        />
        <CardContent>
          {isLockedException && (
            <Alert id="exception_locked" severity="info" sx={{ mb: 2 }}>
              This order can't be edited because the warehouse has already started on it
              {deliveryInfo?.wms_status ? ` (warehouse status: ${deliveryInfo.wms_status})` : ""}. Only exception orders that are still In Pool can be edited.
            </Alert>
          )}
          <GridContainer container spacing={3}>
            <Grid item container md={6} xs={12}>
              <Grid item xs={4}>
                Delivery Name
              </Grid>
              <Grid item xs={8}>
                <InfoText id="ship_to_name">{deliveryInfo?.ship_to_name}</InfoText>
              </Grid>
            </Grid>
            <Grid item container md={6} xs={12}>
              <Grid item xs={4}>
                Order Number
              </Grid>
              <Grid item xs={8}>
                <InfoText id="erp_order">{deliveryInfo?.erp_order}</InfoText>
              </Grid>
            </Grid>

            <Grid item container md={6} xs={12}>
              <Grid item xs={4}>
                Consolidated
              </Grid>
              <Grid item xs={8}>
                <InfoText id="consolidated">{deliveryInfo?.consolidated}</InfoText>
              </Grid>
            </Grid>
            <Grid item container md={6} xs={12}>
              <Grid item xs={4}>
                Order Status
              </Grid>
              <Grid item xs={8}>
                <StatusComponent id="order_status" status={deliveryInfo?.order_status} />
              </Grid>
            </Grid>

            {deliveryInfo?.order_status == "Exception" && (
              <>
                <Grid item container md={6} xs={12}>
                  <Grid item xs={4}>
                    Exception Type
                  </Grid>
                  <Grid item xs={8}>
                    <InfoText id="exception_type">{deliveryInfo?.exception_type}</InfoText>
                  </Grid>
                </Grid>
                <Grid item container md={6} xs={12}>
                  <Grid item xs={4}>
                    Exception Description
                  </Grid>
                  <Grid item xs={8}>
                    <InfoText id="exception_description">{deliveryInfo?.exception_description}</InfoText>
                  </Grid>
                </Grid>
              </>
            )}

            <Grid item container md={6} xs={12}>
              <Grid item xs={4}>
                Company
              </Grid>
              <Grid item xs={8}>
                <InfoText id="company">{deliveryInfo?.company}</InfoText>
              </Grid>
            </Grid>
            <Grid item container md={6} xs={12}>
              <Grid item xs={4}>
                Warehouse
              </Grid>
              <Grid item xs={8}>
                <InfoText id="warehouse">{deliveryInfo?.warehouse}</InfoText>
              </Grid>
            </Grid>

            <Grid item container md={6} xs={12}>
              <Grid item xs={4}>
                Service
              </Grid>
              <Grid item xs={8}>
                <InfoText id="carrier_service">{deliveryInfo?.carrier_service}</InfoText>
              </Grid>
            </Grid>
            <Grid item container md={6} xs={12}>
              <Grid item xs={4}>
                Carrier
              </Grid>
              <Grid item xs={8}>
                <InfoText id="carrier">{deliveryInfo?.carrier}</InfoText>
              </Grid>
            </Grid>

            <Grid item container md={6} xs={12}>
              <Grid item xs={4}>
                Delivery Tracking Number
              </Grid>
              <Grid item xs={8}>
                <InfoText id="tracking_num">{deliveryInfo?.tracking_num}</InfoText>
              </Grid>
            </Grid>
            <Grid item container md={6} xs={12}>
              <Grid item xs={4}>
                Delivery Code
              </Grid>
              <Grid item xs={8}>
                <InfoText id="delivery_code">{deliveryInfo?.delivery_code}</InfoText>
              </Grid>
            </Grid>

            <Grid item container md={6} xs={12}>
              <Grid item xs={4}>
                Brand Code
              </Grid>
              <Grid item xs={8}>
                <InfoText id="brand_code">{deliveryInfo?.brand_code}</InfoText>
              </Grid>
            </Grid>
            <Grid item container md={6} xs={12}>
              <Grid item xs={4}>
                Last Update Date
              </Grid>
              <Grid item xs={8}>
                <InfoText>{formatStandardDate(deliveryInfo?.last_update_datetime)}</InfoText>
              </Grid>
            </Grid>
          </GridContainer>
        </CardContent>
      </Card>
      <Card>
        <CardHeader title="Shipping information" />
        <CardContent>
          <GridContainer container spacing={3}>
            <Grid item container md={6} xs={12}>
              <Grid item xs={4}>
                Company Name
              </Grid>
              <Grid item xs={8}>
                <InfoText id="ship_to">{deliveryInfo?.ship_to}</InfoText>
              </Grid>
            </Grid>
            <Grid item container md={6} xs={12}>
              <Grid item xs={4}>
                Shipping Method
              </Grid>
              <Grid item xs={8}>
                <InfoText id="shipping_method">{deliveryInfo?.shipping_method}</InfoText>
              </Grid>
            </Grid>

            <Grid item container md={6} xs={12}>
              <Grid item xs={4}>
                Destination Country
              </Grid>
              <Grid item xs={8}>
                <InfoText id="ship_to_country">{rootStore.getTextByValue("COUNTRY", deliveryInfo?.ship_to_country)}</InfoText>
              </Grid>
            </Grid>
            <Grid item container md={6} xs={12}>
              <Grid item xs={4}>
                Delivery State
              </Grid>
              <Grid item xs={8}>
                <InfoText id="ship_to_state">{deliveryInfo?.ship_to_state}</InfoText>
              </Grid>
            </Grid>

            <Grid item container md={6} xs={12}>
              <Grid item xs={4}>
                Delivery Suburb
              </Grid>
              <Grid item xs={8}>
                <InfoText id="ship_to_city">{deliveryInfo?.ship_to_city}</InfoText>
              </Grid>
            </Grid>
            <Grid item container md={6} xs={12}>
              <Grid item xs={4}>
                Delivery Post Code
              </Grid>
              <Grid item xs={8}>
                <InfoText id="ship_to_postal_code">{deliveryInfo?.ship_to_postal_code}</InfoText>
              </Grid>
            </Grid>

            <Grid item container md={6} xs={12}>
              <Grid item xs={4}>
                Delivery Address 1
              </Grid>
              <Grid item xs={8}>
                <InfoText id="ship_to_address1">{deliveryInfo?.ship_to_address1}</InfoText>
              </Grid>
            </Grid>
            <Grid item container md={6} xs={12}>
              <Grid item xs={4}>
                Delivery Address 2
              </Grid>
              <Grid item xs={8}>
                <InfoText id="ship_to_address2">{deliveryInfo?.ship_to_address2}</InfoText>
              </Grid>
            </Grid>

            <Grid item container md={6} xs={12}>
              <Grid item xs={4}>
                Delivery Email Address
              </Grid>
              <Grid item xs={8}>
                <InfoText id="ship_to_email_address">{deliveryInfo?.ship_to_email_address}</InfoText>
              </Grid>
            </Grid>
            <Grid item container md={6} xs={12}>
              <Grid item xs={4}>
                Delivery Phone Number
              </Grid>
              <Grid item xs={8}>
                <InfoText id="ship_to_phone_num">{deliveryInfo?.ship_to_phone_num}</InfoText>
              </Grid>
            </Grid>

            <Grid item container md={6} xs={12}>
              <Grid item xs={4}>
                Sales Channel
              </Grid>
              <Grid item xs={8}>
                <InfoText id="sales_channel">{deliveryInfo?.sales_channel}</InfoText>
              </Grid>
            </Grid>

            <Grid item container md={6} xs={12}>
              <Grid item xs={4}>
                Incoterm
              </Grid>
              <Grid item xs={8}>
                <InfoText id="incoterm">{deliveryInfo?.incoterm}</InfoText>
              </Grid>
            </Grid>

            <Grid item container md={6} xs={12}>
              <Grid item xs={4}>
                Ship to Fax Num
              </Grid>
              <Grid item xs={8}>
                <InfoText id="special_details_2">{deliveryInfo?.special_details_2}</InfoText>
              </Grid>
            </Grid>
            <Grid item container md={6} xs={12}>
              <Grid item xs={4}>
                Delivery Instructions
              </Grid>
              <Grid item xs={8}>
                <InfoText id="delivery_instructions">{deliveryInfo?.delivery_instructions}</InfoText>
              </Grid>
            </Grid>

            <Grid item container md={6} xs={12}>
              <Grid item xs={4}>
                Delivery Attention To
              </Grid>
              <Grid item xs={8}>
                <InfoText id="special_details_3">{deliveryInfo?.special_details_3}</InfoText>
              </Grid>
            </Grid>
            <Grid item container md={6} xs={12}></Grid>
          </GridContainer>
        </CardContent>
      </Card>
      <Card>
        <CardHeader title="Customer information" />
        <CardContent>
          <GridContainer container spacing={3}>
            <Grid item container md={6} xs={12}>
              <Grid item xs={4}>
                Customer Name
              </Grid>
              <Grid item xs={8}>
                <InfoText id="customer_name">{deliveryInfo?.customer_name}</InfoText>
              </Grid>
            </Grid>
            <Grid item container md={6} xs={12}>
              <Grid item xs={4}>
                Ship to Code
              </Grid>
              <Grid item xs={8}>
                <InfoText id="ship_to_code">{deliveryInfo?.ship_to_code}</InfoText>
              </Grid>
            </Grid>

            <Grid item container md={6} xs={12}>
              <Grid item xs={4}>
                Customer Country
              </Grid>
              <Grid item xs={8}>
                <InfoText id="customer_country">{rootStore.getTextByValue("COUNTRY", deliveryInfo?.customer_country)}</InfoText>
              </Grid>
            </Grid>
            <Grid item container md={6} xs={12}>
              <Grid item xs={4}>
                Customer Suburb
              </Grid>
              <Grid item xs={8}>
                <InfoText id="customer_city">{deliveryInfo?.customer_city}</InfoText>
              </Grid>
            </Grid>

            <Grid item container md={6} xs={12}>
              <Grid item xs={4}>
                Customer State
              </Grid>
              <Grid item xs={8}>
                <InfoText id="customer_state">{deliveryInfo?.customer_state}</InfoText>
              </Grid>
            </Grid>
            <Grid item container md={6} xs={12}>
              <Grid item xs={4}>
                Customer Postal Code
              </Grid>
              <Grid item xs={8}>
                <InfoText id="customer_postal_code">{deliveryInfo?.customer_postal_code}</InfoText>
              </Grid>
            </Grid>

            <Grid item container md={6} xs={12}>
              <Grid item xs={4}>
                Customer Address 1
              </Grid>
              <Grid item xs={8}>
                <InfoText id="customer_address1">{deliveryInfo?.customer_address1}</InfoText>
              </Grid>
            </Grid>
            <Grid item container md={6} xs={12}>
              <Grid item xs={4}>
                Customer Address 2
              </Grid>
              <Grid item xs={8}>
                <InfoText id="customer_address2">{deliveryInfo?.customer_address2}</InfoText>
              </Grid>
            </Grid>

            <Grid item container md={6} xs={12}>
              <Grid item xs={4}>
                Customer Email Address
              </Grid>
              <Grid item xs={8}>
                <InfoText id="customer_email_address">{deliveryInfo?.customer_email_address}</InfoText>
              </Grid>
            </Grid>
            <Grid item container md={6} xs={12}>
              <Grid item xs={4}>
                Customer Code
              </Grid>
              <Grid item xs={8}>
                <InfoText id="customer_code">{deliveryInfo?.customer_code}</InfoText>
              </Grid>
            </Grid>
          </GridContainer>
        </CardContent>
      </Card>
      <ConfirmDialog open={openDelete} onClose={() => setOpenDelete(false)} onConfirm={onConfirmDelete} />
      <AlertDialog open={openAlert} content={alertContent} onClose={() => setOpenAlert(false)} />
    </>
  )
}

export default SaleOrderDeliveryInfo
